import React, { useContext, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { DataList } from '../../components/DataList'
import { GLOBALS } from '../../utils/Globals'
import { VirtualMachineService } from '../../services/VirtualMachine'
import { SchedulerService } from '../../services/Scheduler'
import { GuacamoleService } from '../../services/Guacamole'
import { Context } from '../../Context'
import { VirtualMachineType } from '../../components/VirtualMachineType'
import { Layout } from '../../components/Layout'
import { FullScreenModal } from '../../components/FullScreeModal'
import RemoteClient from '../../components/RemoteClient'
import { Message } from 'primereact/message'
import './styles.scss'

export const VirtualMachineDataList = () => {
  const { isAuth: token } = useContext(Context)
  const dt = useRef(null)
  const [modals, setModals] = useState([])
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [vmSelectedName, setVmselectedName] = useState(null)
  const { DateTime } = require('luxon')
  const growlRef = useRef(null)

  const ipBodyTemplate = (rowData) => `${rowData.ip}:${rowData.port}`

  const typeBodyTemplate = (rowData) => {
    return <VirtualMachineType type={rowData.type ? rowData.type.id : null} />
  }

  const showGrowl = (severity, summary, detail) => {
    growlRef.current.show({
      severity,
      summary,
      detail,
    })
  }

  const openModal = async (row) => {
    const startDate = DateTime.now().toISO()
    const endDate = DateTime.now().plus({ hours: 1 }).toISO()
    setVmselectedName(row.display_name)

    const isAvaiblePod = await SchedulerService.getAvaiblePod(startDate, endDate, row.pod_id, token)
    if (isAvaiblePod) {
      const existingModal = modals.find((modal) => modal.id === row.id)
      if (existingModal) {
        restoreModal(row.id)
      } else {
        fetchData(row.id).then((encryptedToken) => {
          setModals((prevModals) => [
            ...prevModals,
            { id: row.id, title: row.display_name, encryptedToken, minimized: false },
          ])
        })
      }
    } else {
      showGrowl(
        'warn',
        'Atención!',
        'El POD del recurso selecionado se encuentra reservado, intente mas tarde'
      )
    }
  }

  const closeModal = (id) => {
    setModals((prevModals) => prevModals.filter((modal) => modal.id !== id))
  }

  const minimizeModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) => (modal.id === id ? { ...modal, minimized: true } : modal))
    )
  }

  const restoreModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) => (modal.id === id ? { ...modal, minimized: false } : modal))
    )
  }

  const fetchData = async (resourceId) => {
    setIsLoading(true)
    try {
      const virtualMachineSelected = await VirtualMachineService.getById(resourceId, token)

      const WsUrlGenerated = await GuacamoleService.getEncryptToken(
        TransformVirtualMachineRequest(virtualMachineSelected),
        token
      )

      return WsUrlGenerated.encryptedToken
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  const columns = [
    {
      field: 'display_name',
      header: 'Nombre',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por nombre',
    },
    {
      field: 'ip',
      header: 'IP',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por IP',
      body: ipBodyTemplate,
    },
    {
      field: 'type.name',
      filterField: 'type.name',
      header: 'Tipo',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por Tipo',
      body: typeBodyTemplate,
    },
    {
      field: 'details',
      header: 'Detalles',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por Detalles',
    },
    {
      field: 'pod_id',
      header: 'Pod',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por Pod',
    },
  ]

  const TransformVirtualMachineRequest = (virtualMachineSelected) => {
    let connectionType
    switch (virtualMachineSelected.connection_type) {
      case 'R':
        connectionType = 'rdp'
        break
      case 'S':
        connectionType = 'ssh'
        break
      case 'V':
        connectionType = 'vnc'
        break
      case 'T':
        connectionType = 'telnet'
        break
      default:
        connectionType = 'ssh'
    }

    return {
      secretKey: 'GvfTBBRdtoPqk4FzxcXrIPxqnRSz0MZH',
      tokenObject: {
        connection: {
          type: connectionType,
          settings: {
            hostname: virtualMachineSelected.ip,
            port: virtualMachineSelected.port,
            username: virtualMachineSelected.username,
            password: virtualMachineSelected.password,
            'enable-drive': true,
            'create-drive-path': true,
            security: 'any',
            'ignore-cert': true,
            'enable-wallpaper': false,
          },
        },
      },
    }
  }

  const customActionButtons = ({ row }) => {
    return (
      <Button
        onClick={() => openModal(row)}
        type="button"
        icon="pi pi-window-maximize"
        className="p-button-primary"
        tooltip="Ver"
      ></Button>
    )
  }

  return (
    <Layout>
      {isLoading ? (
        <div className="loading-container">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
        </div>
      ) : error ? (
        <Message severity="error" text={error}></Message>
      ) : (
        <>
          {/* Renderiza cada modal abierta */}
          {modals.map(
            (modal) =>
              !modal.minimized && (
                <FullScreenModal
                  key={modal.id}
                  isOpen={!modal.minimized}
                  onClose={() => closeModal(modal.id)}
                  onMinimize={() => minimizeModal(modal.id)}
                >
                  <RemoteClient
                    encryptedToken={modal.encryptedToken}
                    isMinimized={modal.minimized}
                    vmSelectedName={vmSelectedName}
                  />
                </FullScreenModal>
              )
          )}

          {/* Barra de ventanas minimizadas */}
          <div className="minimized-windows-bar">
            {modals
              .filter((modal) => modal.minimized)
              .map((modal) => (
                <button key={modal.id} onClick={() => restoreModal(modal.id)}>
                  {modal.title}
                </button>
              ))}
          </div>

          <DataList
            ref={dt}
            menuItem={GLOBALS.menu.remoteresource}
            listItems={() => VirtualMachineService.getAll(token)}
            bulkDelete={(ids) => VirtualMachineService.bulkDelete(ids, token)}
            columns={columns}
            customActionButtons={customActionButtons}
          ></DataList>
        </>
      )}
    </Layout>
  )
}
